import React, { useEffect, useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
    Box,
    Button,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone"
import "react-phone-input-2/lib/bootstrap.css";
import { paymentTermsOptions } from "../../../../../constants/data";
import CustomInput from "../../..//../../components/CustomInput";
import style from "./style";
import { useLocation, useNavigate } from "react-router-dom";
import AuthService from "../../../../../api/AuthService";
import { useAlert } from "../../../../../context/AlertContext";
import { getDueDateFromPaymentTerm } from "../../../../../utils/Utilities";
dayjs.extend(utc);
dayjs.extend(timezone);

const ConvertToBill = () => {
    const { showAlert } = useAlert();
    const navigate = useNavigate()
    const location = useLocation();
    const purchaseOrder = location.state || {};

    const itemList = location.state?.itemList || [];
    const [product, setProduct] = useState(
        purchaseOrder?.itemInformation
            ? purchaseOrder.itemInformation.map(item => ({
                name: item.name,
                productId: item.itemId,
                desc: item.description,
                unit: item.rate,
                ordered: item.quantity,
                amount: "",
                billed: item.billed,
                quantity: '',
            }))
            : [{ name: "", productId: "", desc: "", unit: "", ordered: "", quantity: "", amount: "", billed: "" }]
    );
    const [totalAmount, setTotalAmount] = useState(0);
    const [billDate, setBillDate] = useState(dayjs());
    const [paymentTerms, setPaymentTerms] = useState("");
    const [dueDate, setDueDate] = useState();
    const [billNo, setBillNo] = useState("");
    const [orderNo, setOrderNo] = useState(purchaseOrder?.purchaseOrderNumber || "");
    const [discountPercentage, setDiscountPercentage] = useState(0);
    const [taxPercentage, setTaxPercentage] = useState(0);

    useEffect(() => {
        if (purchaseOrder) {
            setOrderNo(purchaseOrder.purchaseOrderNumber || "");

        }
    }, [purchaseOrder]);

    const handleAddField = () => {
        setProduct([
            ...product,
            { name: "", productId: "", desc: "", unit: "", quantity: "", amount: "" },
        ]);
    };

    const handleProduct = (value, index, key) => {
        const updatedProductDetails = [...product];
        updatedProductDetails[index][key] = value;

        if (key === "quantity" || key === "unit") {
            const quantity = Number(updatedProductDetails[index].quantity) || 0;
            const unit = Number(updatedProductDetails[index].unit) || 0;
            updatedProductDetails[index].amount = quantity * unit;
        }

        setProduct(updatedProductDetails);
        calculateTotalAmount();
    };

    const handleDeleteField = (index) => {
        const updatedProduct = [...product];
        updatedProduct.splice(index, 1);
        setProduct(updatedProduct);

        let total = 0;
        updatedProduct.forEach(item => {
            total += Number(item.amount) || 0;
        });
        setTotalAmount(total);
    };

    const calculateTotalAmount = () => {
        let total = 0;
        product.forEach(item => {
            total += Number(item.amount) || 0;
        });
        setTotalAmount(total);
    };

    const calculateFinalAmounts = () => {
        const discountValue = totalAmount * (discountPercentage / 100);
        const subtotalAfterDiscount = totalAmount - discountValue;
        const taxValue = subtotalAfterDiscount * (taxPercentage / 100);
        const grandTotal = subtotalAfterDiscount + taxValue;

        return {
            discountValue,
            taxValue,
            grandTotal
        };
    };

    const handlePaymentTermSelection = (value) => {
        setPaymentTerms(value);
        const dueDate = getDueDateFromPaymentTerm(value, billDate);
        if (dueDate?.isValid()) {
            setDueDate(dueDate.endOf("day"));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const { discountValue, taxValue, grandTotal } = calculateFinalAmounts();

        const productInformation = product.map(item => ({
            productId: item.productId,
            name: item.name,
            description: item.desc,
            unitCost: item.unit,
            quantity: item.quantity,
            amount: item.amount
        }));

        const payload = {
            billDate: dayjs(billDate).format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
            billNo: billNo || `BIL-${Math.floor(1000 + Math.random() * 9000)}`,
            orderNo: orderNo,
            paymentTerms: paymentTerms,
            dueDate: dayjs(dueDate).format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
            productInformation: productInformation,
            total: totalAmount,
            discountPercentage: discountPercentage,
            discountValue: discountValue,
            taxPercentage: taxPercentage,
            taxValue: taxValue,
            grandTotal: grandTotal
        };
        try {
            const result = await AuthService.handleAddNewBill(purchaseOrder._id, payload);
            if (result?.success) {
                showAlert("Bill created successfully!", "success");
                navigate("/dashboard/purchaseOrder");
            } else {
                showAlert(result?.message || "Failed to create bill", "error");
            }
        } catch (error) {
            showAlert("An error occurred while creating bill", "error");
        }
    };

    const { discountValue, taxValue, grandTotal } = calculateFinalAmounts();

    return (
        <>
            <Box px="15px" mt={2}>
                <Box sx={style.headerContainer}>
                    <Typography sx={style.leaveHeading}>New Bill</Typography>
                </Box>
            </Box>
            <Box sx={{ ...style.itemSection, marginTop: 1 }}>
                <Box sx={{ backgroundColor: "#28AA4A", width: "2%", height: 2 }}></Box>
                <Typography sx={style.gridText}>Bill Details</Typography>
            </Box>
            <form onSubmit={(e) => handleSubmit(e)}>
                <Grid container sx={style.mainContainer}>
                    {/* --- Bill Details */}
                    <Grid item xs={12} lg={11.8} sx={style.gridbox}>
                        <Box sx={style.inputSection}>
                            <Box sx={style.inputBox}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <InputLabel sx={style.label}>Bill Date</InputLabel>
                                    <DatePicker
                                        value={billDate}
                                        onChange={(newValue) => {
                                            setBillDate(newValue);
                                        }}
                                        sx={style.dateInput}
                                    />
                                </LocalizationProvider>
                            </Box>

                            <Box sx={style.inputBox}>
                                <FormControl
                                    fullWidth
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            height: "2.5em",
                                            marginTop: "0.4em",
                                            borderRadius: "10px",
                                        },
                                    }}
                                >
                                    <Typography sx={style.inputText}>
                                        Payment Terms<span style={{ color: "red" }}>*</span>
                                    </Typography>
                                    <Select
                                        onChange={(event) => handlePaymentTermSelection(event.target.value)}
                                        name="paymentTerms"
                                        value={paymentTerms}
                                        fullWidth
                                        renderValue={(selected) =>
                                            selected ? (
                                                selected
                                            ) : (
                                                <span style={{ color: "#999" }}>
                                                    Select Payment Terms
                                                </span>
                                            )
                                        }
                                    >
                                        <MenuItem disabled value="">
                                            Select Payment Terms
                                        </MenuItem>
                                        {paymentTermsOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            <Box sx={{ ...style.inputBox }}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <InputLabel sx={style.label}>
                                        Due Date<span style={{ color: "red" }}>*</span>
                                    </InputLabel>
                                    <DatePicker
                                        value={dueDate}
                                        minDate={billDate}
                                        onChange={(newValue) => setDueDate(newValue)}
                                        sx={{ ...style.dateInput }}
                                        disabled={paymentTerms !== "Custom"}
                                    />
                                </LocalizationProvider>
                            </Box>
                        </Box>

                        <Box
                            sx={{ ...style.inputSection, justifyContent: "start", gap: 3.5 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} lg={4}>
                                    <Box sx={{ ...style.inputBox, width: "100%" }}>
                                        <Typography sx={style.inputText}>
                                            Bill Number
                                        </Typography>
                                        <CustomInput
                                            variant="outlined"
                                            required
                                            fullWidth
                                            id="billNo"
                                            placeholder="Bill Number"
                                            error={""}
                                            name="billNo"
                                            value={billNo}
                                            onChange={(e, val) => setBillNo(val)}
                                            autoComplete="billNo"
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12} lg={4}>
                                    <Box sx={{ ...style.inputBox, width: "100%" }}>
                                        <Typography sx={style.inputText}>
                                            Order Number
                                        </Typography>
                                        <CustomInput
                                            variant="outlined"
                                            required
                                            fullWidth
                                            error={""}
                                            placeholder="Order Number"
                                            value={orderNo}
                                            onChange={(e, val) => setOrderNo(val)}
                                            autoComplete="orderNo"
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>

                    {/* --- Product Information */}
                    <Box sx={style.itemSection}>
                        <Box
                            sx={{ backgroundColor: "#28AA4A", width: "2%", height: 2 }}
                        ></Box>
                        <Typography sx={style.gridText}>
                            Product Informations<span style={{ color: "red" }}>*</span>
                        </Typography>
                    </Box>

                    <Grid item xs={12} lg={11.8} sx={style.gridbox}>
                        {product.map((item, index) => {
                            return (
                                <Box sx={style.leaveSection} key={index}>
                                    <Grid container sx={style.itemGrid}>
                                        <Grid item xs={12} lg={2}>
                                            <CustomInput
                                                placeholder="Product Name"
                                                defaultValue={item.name}
                                                value={item.name}
                                                variant="outlined"
                                                error={""}
                                                readOnly={true}
                                                sx={{ ...style.modalInput, ...style.inputSpace }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} lg={2}>
                                            <CustomInput
                                                variant="outlined"
                                                fullWidth
                                                id="description"
                                                placeholder="Description"
                                                defaultValue={item.desc}
                                                value={item.desc}
                                                error={""}
                                                name="desc"
                                                onChange={(e, value) => handleProduct(value, index, "desc")}
                                                autoComplete="desc"
                                            />
                                        </Grid>
                                        <Grid item xs={12} lg={1}>
                                            <CustomInput
                                                variant="outlined"
                                                placeholder="Ordered"
                                                defaultValue={item.ordered}
                                                name="ordered"
                                                readOnly={true}
                                                error={""}
                                                value={item.ordered}
                                            />
                                        </Grid>
                                        <Grid item xs={12} lg={1}>
                                            <CustomInput
                                                variant="outlined"
                                                placeholder="Billed"
                                                defaultValue={item.billed}
                                                name="ordered"
                                                readOnly={true}
                                                error={""}
                                                value={item.billed}
                                            />
                                        </Grid>
                                        <Grid item xs={12} lg={1.5}>
                                            <CustomInput
                                                variant="outlined"
                                                placeholder="Unit Cost"
                                                type="number"
                                                defaultValue={item.unit}
                                                error={""}
                                                value={item.unit}
                                                onChange={(e, val) => handleProduct(val, index, "unit")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} lg={1.5}>
                                            <CustomInput
                                                variant="outlined"
                                                placeholder="Quantity"
                                                type="number"
                                                defaultValue={item.quantity}
                                                error={""}
                                                value={item.quantity}
                                                onChange={(e, val) => handleProduct(val, index, "quantity")}
                                            />
                                        </Grid>
                                        <Grid item xs={9} lg={1.5}>
                                            <TextField
                                                fullWidth
                                                variant="outlined"
                                                placeholder="Amount"
                                                value={item.amount}
                                                InputProps={{
                                                    readOnly: true,
                                                    // startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                                    sx: {
                                                        borderRadius: "8px",
                                                        my: 1,
                                                        fontSize: "14px",
                                                        fontWeight: "400",
                                                    },
                                                }}
                                            />
                                        </Grid>
                                        { product.length > 1 &&
                                            (<Grid item xs={1.5} lg={0.5}>
                                                <Box
                                                    sx={style.deleteIcon}
                                                    onClick={() => handleDeleteField(index)}
                                                >
                                                    <DeleteOutlineIcon />
                                                </Box>
                                            </Grid>)
                                        }
                                    </Grid>
                                </Box>
                            );
                        })}
                    </Grid>

                    {/* --- Discount and Tax */}
                    <Grid container sx={style.lastGrid}>
                        <Box sx={style.itemSection}>
                            <Box
                                sx={{ backgroundColor: "#28AA4A", width: "2%", height: 2, marginLeft: '-16px' }}
                            ></Box>
                            <Typography sx={style.gridText}>Discount and Tax(%)</Typography>
                        </Box>
                        <Grid item xs={12} lg={7.6} sx={style.gridboxs}>
                            <Grid container>
                                <Box sx={{ ...style.inputSection, width: "100%", gap: 3.5 }}>
                                    <Box sx={{ ...style.inputBox, width: "50%" }}>
                                        <Typography sx={style.inputText}>
                                            Discount<span style={{ color: "red" }}>*</span>
                                        </Typography>
                                        <CustomInput
                                            variant="outlined"
                                            fullWidth
                                            placeholder="(Discount)"
                                            defaultValue={discountPercentage}
                                            error={""}
                                            type="number"
                                            value={discountPercentage}
                                            onChange={(e, val) => { setDiscountPercentage(Number(val)) }}
                                        />
                                    </Box>
                                    <Box sx={{ ...style.inputBox, width: "50%" }}>
                                        <Typography sx={style.inputText}>Tax</Typography>
                                        <CustomInput
                                            variant="outlined"
                                            fullWidth
                                            type="number"
                                            defaultValue={taxPercentage}
                                            error={""}
                                            placeholder="Tax Percentage"
                                            name="tax"
                                            value={taxPercentage}
                                            onChange={(e, val) => { setTaxPercentage(Number(val)) }}
                                        />
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            lg={4.2}
                            sx={{ ...style.gridboxs, marginTop: "-17px !important" }}
                        >
                            <Box sx={style.leaveSection}>
                                <Box sx={style.inputContainerBox}>
                                    <Box sx={style.key}>
                                        <Typography sx={style.descValueText}>Total</Typography>
                                    </Box>
                                    <Box sx={style.key}>
                                        <Typography sx={style.descValueText}>
                                            {totalAmount?.toFixed(2)}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={style.inputContainerBox}>
                                    <Box sx={style.key}>
                                        <Typography sx={style.descValueText}>Discount</Typography>
                                    </Box>
                                    <Box sx={style.key}>
                                        <Typography sx={style.descValueText}>
                                            -{discountValue?.toFixed(2)} ({discountPercentage}%)
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={style.inputContainerBox}>
                                    <Box sx={style.key}>
                                        <Typography sx={style.descValueText}>Tax</Typography>
                                    </Box>
                                    <Box sx={style.key}>
                                        <Typography sx={style.descValueText}>
                                            +{taxValue?.toFixed(2)} ({taxPercentage}%)
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={style.inputContainerBox}>
                                    <Box sx={style.key}>
                                        <Typography sx={style.descValueTexts}>
                                            Grand Total
                                        </Typography>
                                    </Box>
                                    <Box sx={style.key}>
                                        <Typography sx={style.descValueTexts}>
                                            {grandTotal?.toFixed(2)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>

                <Box sx={style.mainBoxes}>
                    <Box sx={style.createJob}>
                        <Button
                            variant="contained"
                            color="success"
                            type="submit"
                            sx={style.cancelButtons}
                            disabled={!billNo || !paymentTerms || !dueDate || !billDate}
                        >
                            Create Bill
                        </Button>
                    </Box>
                </Box>
            </form>
        </>
    );
};

export default ConvertToBill;